import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import GoogleMap from '@/components/ui/GoogleMap';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { useAuth } from '@/context/AuthContext';
import type { Marker, GoogleMapsMarker } from '@/types';
import api from '@/services/api';

const MapViewPage: React.FC = () => {
  const [markers, setMarkers] = useState<Marker[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMarker, setSelectedMarker] = useState<Marker | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    fetchMarkers();
  }, []);

  const fetchMarkers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/markers/public');
      
      if (response.data.success) {
        setMarkers(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching markers:', error);
      toast.error('Errore nel caricamento delle segnalazioni');
    } finally {
      setLoading(false);
    }
  };

  const handleSupportReport = async (markerId: string, reportId: string) => {
    if (!isAuthenticated) {
      toast.error('Devi essere autenticato per supportare una segnalazione');
      return;
    }

    try {
      const response = await api.post(`/markers/${markerId}/reports/${reportId}/support`);
      
      if (response.data.success) {
        toast.success('Supporto aggiunto con successo!');
        // Update local state
        setMarkers(prev => prev.map(marker => 
          marker._id === markerId ? response.data.data : marker
        ));
        if (selectedMarker?._id === markerId) {
          setSelectedMarker(response.data.data);
        }
      }
    } catch (error: any) {
      console.error('Error adding support:', error);
      const message = error.response?.data?.message || 'Errore nell\'aggiunta del supporto';
      toast.error(message);
    }
  };

  // Convert markers to Google Maps format
  const googleMarkers: GoogleMapsMarker[] = markers.map(marker => ({
    position: {
      latitude: marker.coordinates.latitude,
      longitude: marker.coordinates.longitude
    },
    title: marker.streetName,
    info: `
      <div style="max-width: 300px;">
        <h3 style="margin: 0 0 8px 0; font-size: 14px; font-weight: bold;">
          ${marker.streetName}
        </h3>
        ${marker.reports.length > 0 ? `
          <p style="margin: 4px 0; font-size: 12px; color: #666;">
            ${marker.reports[0].issue === 'no_fiber' ? 'Fibra non disponibile' : 
              marker.reports[0].issue === 'slow_speed' ? 'Velocità bassa' :
              marker.reports[0].issue === 'connection_issues' ? 'Problemi di connessione' : 
              'Altro problema'}
          </p>
          <p style="margin: 4px 0; font-size: 12px; color: #666;">
            Supporti: ${marker.reports[0].supportCount || 1}
          </p>
        ` : ''}
        <button 
          onclick="window.viewMarkerDetails('${marker._id}')"
          style="margin-top: 8px; padding: 4px 8px; font-size: 12px; background: #3b82f6; color: white; border: none; border-radius: 4px; cursor: pointer;"
        >
          Vedi dettagli
        </button>
      </div>
    `
  }));

  // Add global function for marker details
  useEffect(() => {
    (window as any).viewMarkerDetails = (markerId: string) => {
      const marker = markers.find(m => m._id === markerId);
      if (marker) {
        setSelectedMarker(marker);
        setShowReportModal(true);
      }
    };
  }, [markers]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">
              Mappa delle Segnalazioni
            </h1>
            <div className="flex space-x-4">
              {isAuthenticated ? (
                <Link
                  to="/report"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  Nuova Segnalazione
                </Link>
              ) : (
                <Link
                  to="/login"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  Accedi per Segnalare
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="container mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-md p-4">
          <GoogleMap
            center={{ latitude: 41.9028, longitude: 12.4964 }}
            zoom={6}
            height="600px"
            markers={googleMarkers}
          />
        </div>

        {/* Stats */}
        <div className="mt-6 grid md:grid-cols-3 gap-4">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="text-2xl font-bold text-blue-600 mb-2">
              {markers.length}
            </div>
            <div className="text-gray-600">Segnalazioni Totali</div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="text-2xl font-bold text-orange-600 mb-2">
              {markers.filter(m => 
                m.reports.some(r => r.status === 'open' || r.status === 'in_progress')
              ).length}
            </div>
            <div className="text-gray-600">Segnalazioni Aperte</div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="text-2xl font-bold text-green-600 mb-2">
              {markers.filter(m => 
                m.reports.some(r => r.status === 'resolved')
              ).length}
            </div>
            <div className="text-gray-600">Problemi Risolti</div>
          </div>
        </div>
      </div>

      {/* Report Details Modal */}
      {showReportModal && selectedMarker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-gray-900">
                  {selectedMarker.streetName}
                </h2>
                <button
                  onClick={() => setShowReportModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              {selectedMarker.streetNameCorrection && (
                <p className="text-sm text-gray-600 mb-4">
                  <strong>Nome corretto:</strong> {selectedMarker.streetNameCorrection}
                </p>
              )}

              <div className="mb-6">
                <h3 className="font-semibold mb-2">Informazioni Connessione</h3>
                <div className="bg-gray-50 p-3 rounded">
                  <p><strong>Operatore:</strong> {selectedMarker.currentLine.operator || 'N/D'}</p>
                  <p><strong>Tecnologia:</strong> {selectedMarker.currentLine.technology}</p>
                  <p><strong>Velocità:</strong> {selectedMarker.currentLine.maxSpeed.download}/{selectedMarker.currentLine.maxSpeed.upload} {selectedMarker.currentLine.maxSpeed.unit}</p>
                  <p><strong>Attiva:</strong> {selectedMarker.currentLine.isActivated ? 'Sì' : 'No'}</p>
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-3">Segnalazioni</h3>
                <div className="space-y-3">
                  {selectedMarker.reports.map((report) => (
                    <div key={report._id} className="border p-4 rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                            report.status === 'open' ? 'bg-red-100 text-red-800' :
                            report.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                            report.status === 'resolved' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {report.status === 'open' ? 'Aperta' :
                             report.status === 'in_progress' ? 'In Corso' :
                             report.status === 'resolved' ? 'Risolta' : 'Chiusa'}
                          </span>
                          <span className={`ml-2 inline-block px-2 py-1 text-xs rounded-full ${
                            report.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                            report.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                            report.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {report.priority === 'urgent' ? 'Urgente' :
                             report.priority === 'high' ? 'Alta' :
                             report.priority === 'medium' ? 'Media' : 'Bassa'}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500">
                          {new Date(report.reportedAt).toLocaleDateString('it-IT')}
                        </div>
                      </div>

                      <p className="text-sm text-gray-600 mb-2">
                        <strong>Problema:</strong> {
                          report.issue === 'no_fiber' ? 'Fibra non disponibile' :
                          report.issue === 'slow_speed' ? 'Velocità troppo bassa' :
                          report.issue === 'connection_issues' ? 'Problemi di connessione' :
                          report.issue === 'service_unavailable' ? 'Servizio non disponibile' :
                          'Altro'
                        }
                      </p>

                      {report.description && (
                        <p className="text-sm text-gray-600 mb-3">{report.description}</p>
                      )}

                      <div className="flex justify-between items-center">
                        <div className="text-sm text-gray-500">
                          👥 {report.supportCount || 1} person{(report.supportCount || 1) !== 1 ? 'e' : 'a'} ha segnalato questo problema
                        </div>
                        
                        {isAuthenticated && user && (
                          report.supportedBy && !report.supportedBy.includes(user._id) ? (
                            <button
                              onClick={() => handleSupportReport(selectedMarker._id, report._id)}
                              className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded transition-colors"
                            >
                              +1 Anch'io
                            </button>
                          ) : (
                            <span className="text-sm text-green-600">✓ Già supportata</span>
                          )
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapViewPage;